import type { ReactNode, Ref } from 'react'

import { useRef } from 'react'
import { useGSAP } from '@gsap/react'

import { gsap } from '@/lib/gsap'
import { cn } from '@/lib/utils'

interface MagneticProps {
    children: ReactNode
    className?: string
    strength?: number
}

export const Magnetic = ({ children, className, strength = 0.4 }: MagneticProps) => {
    const ref = useRef<HTMLSpanElement>(null)

    useGSAP(
        () => {
            const el = ref.current
            if (!el) return

            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            const coarse = window.matchMedia('(pointer: coarse)').matches
            if (reduce || coarse) return

            const xTo = gsap.quickTo(el, 'x', { duration: 0.55, ease: 'power3' })
            const yTo = gsap.quickTo(el, 'y', { duration: 0.55, ease: 'power3' })

            const onMove = (e: MouseEvent) => {
                const rect = el.getBoundingClientRect()
                const relX = e.clientX - (rect.left + rect.width / 2)
                const relY = e.clientY - (rect.top + rect.height / 2)
                xTo(relX * strength)
                yTo(relY * strength)
            }

            const onLeave = () => {
                xTo(0)
                yTo(0)
            }

            el.addEventListener('mousemove', onMove)
            el.addEventListener('mouseleave', onLeave)

            return () => {
                el.removeEventListener('mousemove', onMove)
                el.removeEventListener('mouseleave', onLeave)
            }
        },
        { scope: ref, dependencies: [] }
    )

    return (
        <span ref={ref as Ref<HTMLSpanElement>} className={cn('inline-block', className)}>
            {children}
        </span>
    )
}
